import {
  CreateUserRequest,
  GroupsListResponse,
  MembershipChange,
  MembershipBatchResponse,
  ResetPasswordResponse,
  UpdateUserRequest,
  UserGroupsResponse,
  UserResponse,
  UsersListResponse,
} from "../types/accountManagement.types";

import { getCachedUsers, invalidateUsersCache, setCachedUsers } from "./accountManagement.cache";
import { apiDelete, apiGet, apiPost, apiPut } from "./apiService";

const endpoints = {
  users: "/account-management/users",
  groups: "/account-management/groups",
  membershipsBatch: "/account-management/memberships/batch",
};

const userPath = (username: string): string =>
  `${endpoints.users}/${encodeURIComponent(username)}`;

interface GetUsersOptions {
  force?: boolean;
}

export const getUsers = async (options?: GetUsersOptions): Promise<UsersListResponse> => {
  if (!options?.force) {
    const cached = getCachedUsers();
    if (cached) return { users: cached };
  }

  const result = await apiGet<UsersListResponse>(endpoints.users);
  setCachedUsers(result.users);
  return result;
};

export const getUser = (username: string): Promise<UserResponse> =>
  apiGet<UserResponse>(userPath(username));

export const createUser = async (body: CreateUserRequest): Promise<UserResponse> => {
  const result = await apiPost<UserResponse>(endpoints.users, body);
  invalidateUsersCache();
  return result;
};

export const updateUser = async (
  username: string,
  body: UpdateUserRequest,
): Promise<UserResponse> => {
  const result = await apiPut<UserResponse>(userPath(username), body);
  invalidateUsersCache();
  return result;
};

export const deleteUser = async (username: string): Promise<void> => {
  await apiDelete(userPath(username));
  invalidateUsersCache();
};

export const enableUser = (username: string): Promise<UserResponse> =>
  apiPost<UserResponse>(`${userPath(username)}/enable`);

export const disableUser = (username: string): Promise<UserResponse> =>
  apiPost<UserResponse>(`${userPath(username)}/disable`);

export const getUserGroups = (username: string): Promise<UserGroupsResponse> =>
  apiGet<UserGroupsResponse>(`${userPath(username)}/groups`);

export const resetPassword = (username: string): Promise<ResetPasswordResponse> =>
  apiPost<ResetPasswordResponse>(`${userPath(username)}/reset-password`);

export const getGroups = (): Promise<GroupsListResponse> =>
  apiGet<GroupsListResponse>(endpoints.groups);

export const batchUpdateMemberships = async (
  changes: MembershipChange[],
): Promise<MembershipBatchResponse> => {
  const result = await apiPost<MembershipBatchResponse>(endpoints.membershipsBatch, { changes });
  invalidateUsersCache();
  return result;
};
